import { useEffect, useRef, useState } from "react";
import { Volume2, Lightbulb, SpellCheck2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { speak } from "@/lib/buddy";

const Done = ({ score, total, stars, onAgain, onExit }) => (
  <div className="text-center py-6" data-testid="spell-game-done">
    <p className="font-display text-3xl text-[#2C4A3E]">You spelled {score} of {total}!</p>
    {stars > 0 && <p className="mt-2 text-[#6B9080] inline-flex items-center gap-1.5" data-testid="spell-game-new-stars"><SpellCheck2 className="w-5 h-5" /> {stars} new spelling star{stars > 1 ? "s" : ""}</p>}
    <div className="mt-6 flex justify-center gap-3">
      <button onClick={onAgain} data-testid="spell-game-again" className="px-6 py-3 rounded-full bg-[#C87D53] text-white">Play again</button>
      <button onClick={onExit} data-testid="spell-game-exit" className="px-6 py-3 rounded-full border border-[#E6DEC3]">Back to my words</button>
    </div>
  </div>
);

const ACCENTS = ["á", "é", "í", "ó", "ú", "ñ", "ü"];

const AccentPad = ({ onKey }) => (
  <div className="w-full flex flex-wrap gap-2" data-testid="spell-accent-pad">
    {ACCENTS.map((a) => <button key={a} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onKey(a)} data-testid={`spell-accent-${a}`}
      className="w-11 h-11 rounded-xl bg-white border border-[#C8DCCB] font-display text-xl text-[#2C4A3E] hover:bg-[#F7F3EB] transition-colors">{a}</button>)}
  </div>
);

export const SpellGame = ({ onExit, api = "/words/spell", spanish = false }) => {
  const { buddy } = useProfile();
  const [g, setG] = useState(null);
  const [i, setI] = useState(0);
  const [text, setText] = useState("");
  const [hint, setHint] = useState(false);
  const [res, setRes] = useState(null);
  const [tally, setTally] = useState({ score: 0, stars: 0 });
  const inputRef = useRef(null);
  const started = useRef(false);
  const start = () => { setG(null); setI(0); setRes(null); setText(""); setHint(false); setTally({ score: 0, stars: 0 });
    http.post(api).then((r) => setG(r.data)).catch((e) => { toast.error(formatApiError(e)); onExit(); }); };
  useEffect(() => { if (started.current) return; started.current = true; start(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const item = g?.items[i];
  const say = () => item && speak(spanish ? `¿Cómo se escribe "${item.say}"? ${item.say}.` : `Spell ${item.say}. ${item.say}.`, buddy?.voice, spanish ? 1 : 0.8, spanish ? "es" : undefined).catch(() => {});
  const addKey = (k) => { const el = inputRef.current; const at = el?.selectionStart ?? text.length; setText(text.slice(0, at) + k + text.slice(el?.selectionEnd ?? at)); requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(at + 1, at + 1); }); };
  useEffect(() => { say(); inputRef.current?.focus(); }, [item?.word_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!g) return <p className="text-stone-500 py-10" data-testid="spell-game-loading">{buddy?.name || "Your buddy"} is choosing words to spell…</p>;
  if (i >= g.items.length) return <Done {...tally} total={g.items.length} onAgain={start} onExit={onExit} />;
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || res) return;
    try {
      const r = (await http.post(`${api}/${g.id}/answer`, { index: i, text })).data;
      setRes(r);
      setTally((t) => ({ score: t.score + r.correct, stars: t.stars + r.mastered }));
      speak(spanish ? (r.correct ? `¡Muy bien! ${r.word}.` : `Se escribe así: ${r.word}.`) : r.correct ? (r.mastered ? `Amazing! A spelling star for ${r.word}!` : "You spelled it!") : `Almost! It's spelled ${r.word.split("").join(", ")}.`, buddy?.voice, 1, spanish ? "es" : undefined).catch(() => {});
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const next = () => { setRes(null); setText(""); setHint(false); setI(i + 1); };
  return (
    <div className="rounded-[2rem] bg-[#EEF3EF] border border-[#C8DCCB] p-6 md:p-8 rise" data-testid="spell-game">
      <div className="flex items-center gap-4">
        <BuddyAvatar buddy={buddy} className="w-16 h-16 shrink-0" />
        <div className="flex-1"><p className="text-xs uppercase tracking-widest text-stone-500">Word {i + 1} of {g.items.length} · {item.length} letters</p>
          <p className="text-stone-700 mt-1" data-testid="spell-game-meaning">{item.meaning}</p></div>
        <button onClick={say} data-testid="spell-game-say" aria-label="Hear the word" className="p-3 rounded-full bg-white border border-[#C8DCCB]"><Volume2 className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="mt-6 flex flex-wrap gap-3">
        <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} disabled={!!res} maxLength={40} autoComplete="off" autoCapitalize="off" spellCheck={false}
          placeholder={hint ? `${item.hint}${"_".repeat(item.length - 1)}` : "Type the word"} data-testid="spell-game-input"
          className="flex-1 min-w-[200px] px-5 py-4 rounded-2xl bg-white border border-[#C8DCCB] font-display text-2xl tracking-[0.15em] text-[#2C4A3E] focus:outline-none focus:border-[#2C4A3E]" />
        {!res && <button type="button" onClick={() => { setHint(true); inputRef.current?.focus(); }} disabled={hint} data-testid="spell-game-hint" className="inline-flex items-center gap-1.5 px-4 py-3 min-h-[48px] rounded-2xl border border-[#C8DCCB] bg-white text-sm disabled:opacity-50"><Lightbulb className="w-4 h-4" /> {hint ? `Starts with "${item.hint}"` : "Hint"}</button>}
        {spanish && !res && <AccentPad onKey={addKey} />}
        {!res && <button disabled={!text.trim()} data-testid="spell-game-check" className="px-6 py-3 min-h-[48px] rounded-2xl bg-[#2C4A3E] text-white disabled:opacity-50">Check</button>}
      </form>
      {res && (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <p className={`flex-1 text-sm inline-flex items-center gap-2 ${res.correct ? "text-[#2C4A3E]" : res.almost ? "text-[#9A4F1C]" : "text-[#A33224]"}`} data-testid="spell-game-feedback">
            {res.correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
            {res.mastered ? "Spelling star! You can spell this one now." : res.correct ? `Correct! ${res.streak} in a row.` : res.almost ? <>Almost! Check the accents: <b className="tracking-widest" data-testid="spell-game-answer">{res.word}</b></> : <>It's spelled <b className="tracking-widest" data-testid="spell-game-answer">{res.word}</b></>}
          </p>
          <button onClick={next} data-testid="spell-game-next" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white">{i + 1 < g.items.length ? "Next word" : "Finish"}</button>
        </div>
      )}
    </div>
  );
};
